import {
  IInternalShopInventoryEvent,
  IInternalShopInventoryItem,
  IInternalShopItem,
} from 'types/income';
import { fromCents } from 'utils/general';
import { converters as tableConverters } from 'store/table';
import { convertBaseShopItem } from 'store/shop';
import { loaders } from './internal-shop.loaders';
import {
  InternalShopInventoryEvent,
  InternalShopInventoryItem,
  InternalShopItem,
} from './internal-shop.types';

const convertInternalShopItem = (item: IInternalShopItem): InternalShopItem => {
  return {
    ...convertBaseShopItem(item),
    prices:
      item.prices
        ?.toSorted((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((price) => ({
          id: price.id,
          price: fromCents(price.price ?? 0),
          translations: item.translations.map((trans) => {
            const priceTranslation = price.translations?.find(
              (priceTrans) => priceTrans.language === trans.language,
            );
            return {
              language: trans.language,
              description: priceTranslation?.description ?? '',
            };
          }),
          inventory: price.inventory ?? 0,
        })) ?? [],
    trackInventory: item.trackInventory,
    purchaseLimit: item.purchaseLimit,
    purchaseLimitVariant: item.purchaseLimitType,
    purchaseLimitPeriod: item.purchaseLimitPeriod,
    notifyOnMerchandisingOrder: item.notifyOnMerchandisingOrder ?? false,
    requireShipping: item.requireShipping,
  };
};

const convertItems = (items: IInternalShopItem[]): InternalShopItem[] => {
  return items.map(convertInternalShopItem);
};

const convertInventoryItem = (
  item: IInternalShopInventoryItem,
): InternalShopInventoryItem => {
  return {
    id: item.variantId,
    name: item.variantName ?? '',
    itemId: item.itemId,
    itemName: item.itemName,
    remainingInventory: item.remainingInventory,
    lowQuantityAlert: item.lowQuantityAlert,
    trackInventory: item.trackInventory,
  };
};

const convertInventoryItems = (
  items: IInternalShopInventoryItem[],
): InternalShopInventoryItem[] => {
  return items.map(convertInventoryItem);
};

const convertInventoryEvent = (
  event: IInternalShopInventoryEvent,
): InternalShopInventoryEvent => {
  return {
    id: event.id,
    variant: event.eventType,
    inventoryChange: event.inventoryChange,
    doneBy: event.doneBy
      ? {
          id: event.doneBy.id,
          name: event.doneBy.name,
          email: event.doneBy.email,
        }
      : null,
    comment: event.comment ?? '',
    createdAt: event.date,
  };
};

const convertInternalShopInventoryEvents = (
  items: IInternalShopInventoryEvent[],
): InternalShopInventoryEvent[] => {
  return items.map(convertInventoryEvent);
};

export const converters = {
  getItems: ({ data }: Awaited<ReturnType<typeof loaders.getItems>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertItems(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getItem: ({ data }: Awaited<ReturnType<typeof loaders.getItem>>) => {
    return convertInternalShopItem(data);
  },
  getInventoryItems: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getInventoryItems>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertInventoryItems(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getInventoryEvents: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getInventoryEvents>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertInternalShopInventoryEvents(data.data),
      meta: {
        inventory: data.inventory,
        itemId: data.itemId,
        itemName: data.itemName,
        variantId: data.variantId,
        translations: data.variantTranslations,
      },
      total,
      page,
      pageSize,
      pages,
    };
  },
};
